/**
 * QuickCreateModal Component
 *
 * Modal dialog that provides quick access to create common resources.
 * Shows options based on user permissions.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FiX, FiClock, FiUsers, FiUser, FiFileText } from 'react-icons/fi';
import '../styles/QuickCreateModal.css';

const QuickCreateModal = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Handle escape key - must be called before any early returns
  React.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, onClose]);

  if (!isOpen || !user) return null;

  const handleOptionClick = (path) => {
    navigate(path);
    onClose();
  };

  // Check if user has a specific permission (handles 'all' permission for admins)
  const hasPermission = (permission) => {
    if (!user || !user.full_role) return false;
    return user.full_role['all'] || user.full_role[permission];
  };

  // Define create options based on permissions
  const createOptions = [
    {
      id: 'time-entry',
      label: 'Create Time Entry',
      icon: FiClock,
      path: '/time_entries/create',
      permission: hasPermission('can_view_time_entries'),
      color: '#0078c1'
    },
    {
      id: 'client',
      label: 'Create Client',
      icon: FiUsers,
      path: '/clients/create',
      permission: hasPermission('can_edit_clients'),
      color: '#10b981'
    },
    {
      id: 'user',
      label: 'Create User',
      icon: FiUser,
      path: '/users/create',
      permission: hasPermission('can_edit_users'),
      color: '#8b5cf6'
    },
    {
      id: 'invoice-text',
      label: 'Create Invoice Text',
      icon: FiFileText,
      path: '/invoice_texts/create',
      permission: hasPermission('can_create_invoices'),
      color: '#f59e0b'
    }
  ];

  // Filter options based on permissions
  const availableOptions = createOptions.filter(option => option.permission);

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="quick-create-overlay" onClick={handleBackdropClick}>
      <div className="quick-create-modal">
        <div className="quick-create-header">
          <h2>Quick Create</h2>
          <button
            className="quick-create-close"
            onClick={onClose}
            aria-label="Close"
          >
            <FiX />
          </button>
        </div>

        <div className="quick-create-content">
          {availableOptions.length > 0 ? (
            <div className="quick-create-grid">
              {availableOptions.map((option) => {
                const Icon = option.icon;
                return (
                  <button
                    key={option.id}
                    className="quick-create-option"
                    onClick={() => handleOptionClick(option.path)}
                    style={{ '--option-color': option.color }}
                  >
                    <div className="option-icon">
                      <Icon size={24} />
                    </div>
                    <span className="option-label">{option.label}</span>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="quick-create-empty">
              <p>No create permissions available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickCreateModal;
